import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, stagger, fadeUp } from "@/lib/motion";
import { PORTFOLIO } from "@/lib/site-data";
import { scrollToSection } from "@/lib/scroll";
import { emitPrefill } from "@/lib/prefill";

// Map sector → matching option in the Contact form's website-type select
const SLUG_TO_TYPE = {
  "landscaping": "Business Website",
  "restaurants": "Business Website",
  "consulting": "Business Website",
  "fitness": "Business Website",
  "local-services": "Business Website",
  "e-commerce": "E-commerce Website",
};

const Portfolio = () => {
  const startProject = (p) => {
    emitPrefill({
      website_type: SLUG_TO_TYPE[p.slug] || "Business Website",
      project_details: `I'd love a website for a ${p.category} business — please reach out to discuss.`,
    });
    scrollToSection("contact");
  };

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32"
    >
      <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <span className="eyebrow">Sectors we work in</span>
          <h2 className="mt-5 font-heading text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl">
            Industries we love to build for
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/50 md:text-base">
            Every industry has its own rhythm. Here are the sectors we specialise in —
            tap one to start a project of your own.
          </p>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-white/55 md:text-base">
          From local services to online retail, we tailor every site to the way each
          sector actually wins customers.
        </p>
      </Reveal>

      <motion.div
        variants={stagger(0.08)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        {PORTFOLIO.map((p) => (
          <motion.article
            key={p.slug}
            variants={fadeUp}
            data-testid={`sector-card-${p.slug}`}
            onClick={() => startProject(p)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                startProject(p);
              }
            }}
            className="group relative cursor-pointer overflow-hidden rounded-3xl border border-white/10 bg-ink-soft transition-all duration-300 hover:-translate-y-1 hover:border-white/25 focus:outline-none focus-visible:border-neon-cyan/50"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={p.image}
                alt={`${p.category} website — sector we build for`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent opacity-95" />
            </div>

            <div className="relative -mt-16 p-6">
              <h3 className="font-heading text-xl font-semibold leading-tight text-white">
                {p.category}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55">
                {p.description}
              </p>
              <div
                data-testid={`sector-cta-${p.slug}`}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-white transition-colors duration-300 group-hover:border-neon-cyan/50 group-hover:text-neon-cyan"
              >
                Start a project in this sector
                <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </motion.article>
        ))}
      </motion.div>
    </section>
  );
};

export default Portfolio;
